import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { getNexusClient } from '@/lib/supabase'
import { AdminUserRow } from '@/lib/supabase/types'

interface AdminUser {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: 'super_admin' | 'content_manager' | 'support'
  avatarUrl: string | null
  isActive: boolean
}

interface AuthContextType {
  user: User | null
  adminUser: AdminUser | null
  session: Session | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [initAttempted, setInitAttempted] = useState(false)

  const fetchAdminUser = useCallback(async (userId: string, userEmail?: string) => {
    console.log('🔐 fetchAdminUser: Looking up user:', userId)
    try {
      const supabase = getNexusClient()
      console.log('🔐 fetchAdminUser: Querying admin_users table...')

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<{ data: null; error: { message: string; code: string } }>((resolve) => {
        setTimeout(() => {
          console.warn('🔐 fetchAdminUser: Query timed out after 3s')
          resolve({ data: null, error: { message: 'Query timeout', code: 'TIMEOUT' } })
        }, 3000)
      })

      const queryPromise = supabase
        .from('admin_users')
        .select('*')
        .eq('id', userId)
        .single<AdminUserRow>()

      const { data, error } = await Promise.race([queryPromise, timeoutPromise])

      console.log('🔐 fetchAdminUser: Response:', { data, error })

      if (error) {
        console.error('🔐 fetchAdminUser: Error:', error.message, error.code)
        // DEV MODE: If admin_users table doesn't exist, user not found, or timeout,
        // create a temporary admin user to allow access during development
        if (error.code === 'PGRST116' || error.code === '42P01' || error.code === 'PGRST301' || error.code === 'TIMEOUT') {
          console.warn('🔐 fetchAdminUser: DEV MODE - Creating temporary admin user')
          console.warn('⚠️ Run supabase-setup.sql in your Nexus Admin Supabase to set up properly')
          return {
            id: userId,
            email: userEmail || 'dev@nexus.local',
            firstName: 'Dev',
            lastName: 'Admin',
            role: 'super_admin' as const,
            avatarUrl: null,
            isActive: true,
          }
        }
        return null
      }

      if (data) {
        const adminUser: AdminUser = {
          id: data.id,
          email: data.email,
          firstName: data.first_name,
          lastName: data.last_name,
          role: data.role,
          avatarUrl: data.avatar_url,
          isActive: data.is_active,
        }
        console.log('🔐 fetchAdminUser: Found admin user:', adminUser)
        return adminUser
      }
      console.log('🔐 fetchAdminUser: No data returned')
      return null
    } catch (err) {
      console.error('🔐 fetchAdminUser: Exception:', err)
      // DEV MODE: Allow access even on exceptions
      console.warn('🔐 fetchAdminUser: DEV MODE - Creating temporary admin user due to exception')
      return {
        id: userId,
        email: userEmail || 'dev@nexus.local',
        firstName: 'Dev',
        lastName: 'Admin',
        role: 'super_admin' as const,
        avatarUrl: null,
        isActive: true,
      }
    }
  }, [])

  useEffect(() => {
    // Prevent duplicate initialization
    if (initAttempted) {
      console.log('🔐 AuthProvider: Already initialized, skipping')
      return
    }
    setInitAttempted(true)

    console.log('🔐 AuthProvider: Initializing...')

    let supabase: ReturnType<typeof getNexusClient>
    try {
      supabase = getNexusClient()
      console.log('🔐 AuthProvider: Got Nexus client')
    } catch (err) {
      console.error('🔐 AuthProvider: Failed to get client:', err)
      setIsLoading(false)
      return
    }

    // Add timeout to detect hanging - reduced to 3s
    const timeoutId = setTimeout(() => {
      console.warn('🔐 AuthProvider: getSession() timed out after 3s - continuing without session')
      setIsLoading(false)
    }, 3000)

    // Get initial session
    console.log('🔐 AuthProvider: Calling getSession()...')
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      clearTimeout(timeoutId)
      console.log('🔐 AuthProvider: Got session:', session ? 'YES' : 'NO')
      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        console.log('🔐 AuthProvider: Fetching admin user for:', session.user.id)
        const admin = await fetchAdminUser(session.user.id, session.user.email)
        console.log('🔐 AuthProvider: Admin user:', admin)
        setAdminUser(admin)
      }

      console.log('🔐 AuthProvider: Done loading')
      setIsLoading(false)
    }).catch(err => {
      clearTimeout(timeoutId)
      console.error('🔐 AuthProvider: Error getting session:', err)
      setIsLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        const admin = await fetchAdminUser(session.user.id, session.user.email)
        setAdminUser(admin)
      } else {
        setAdminUser(null)
      }

      setIsLoading(false)
    })

    return () => {
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  const signIn = async (email: string, password: string) => {
    console.log('🔐 signIn: Attempting login for:', email)
    try {
      const supabase = getNexusClient()
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        console.error('🔐 signIn: Error:', error.message)
        return { error }
      }

      console.log('🔐 signIn: Success! User:', data.user?.id)

      // Manually fetch admin user after successful sign in
      if (data.user) {
        const admin = await fetchAdminUser(data.user.id, data.user.email)
        console.log('🔐 signIn: Admin user:', admin)
        setUser(data.user)
        setSession(data.session)
        setAdminUser(admin)
      }

      return { error: null }
    } catch (err) {
      console.error('🔐 signIn: Exception:', err)
      return { error: err as Error }
    }
  }

  const signOut = async () => {
    const supabase = getNexusClient()
    await supabase.auth.signOut()
    setUser(null)
    setAdminUser(null)
    setSession(null)
  }

  const refreshSession = async () => {
    const supabase = getNexusClient()
    const { data } = await supabase.auth.refreshSession()
    if (data.session) {
      setSession(data.session)
      setUser(data.session.user)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        adminUser,
        session,
        isLoading,
        isAuthenticated: !!user && !!adminUser?.isActive,
        signIn,
        signOut,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
